import { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, Plus, Trash2, Edit, X, Download, Upload, Shield, History, PlayCircle, Mail, RotateCcw, AlertTriangle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import PasswordInput from '../components/PasswordInput';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GestaoPage() {
  const { user, token } = useAuth();
  const isAdmin = user?.role === 'admin';
  const canManageUsers = user?.role === 'admin' || user?.role === 'tecnico';
  const [users, setUsers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(null);
  const [form, setForm] = useState({ nome: '', email: '', password: '', tipo: 'operacional' });
  const [editForm, setEditForm] = useState({ nome: '', email: '', tipo: '', novaPassword: '' });
  const [msg, setMsg] = useState('');
  const [backups, setBackups] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [showAudit, setShowAudit] = useState(false);

  const carregar = () => {
    axios.get(`${API}/auth/listar`).then(r => setUsers(r.data)).catch(console.error);
    if (isAdmin) {
      axios.get(`${API}/backup/auto/listar`).then(r => setBackups(r.data)).catch(() => {});
    }
  };
  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, []);

  const criar = async (e) => {
    e.preventDefault();
    setMsg('');
    try {
      const r = await axios.post(`${API}/auth/criar-utilizador`, form);
      setMsg(r.data.mensagem);
      setForm({ nome: '', email: '', password: '', tipo: 'operacional' });
      setShowCreate(false);
      carregar();
    } catch (err) { setMsg(err.response?.data?.detail || 'Erro ao criar'); }
  };

  const editar = async () => {
    try {
      // Só enviar novaPassword se foi explicitamente preenchido.
      // Evita que o backend re-hashe a password em cada edição.
      const payload = {
        nome: editForm.nome,
        email: editForm.email,
        tipo: editForm.tipo,
      };
      const novaPwd = (editForm.novaPassword || '').trim();
      if (novaPwd) payload.novaPassword = novaPwd;
      await axios.put(`${API}/auth/editar/${showEdit.id}`, payload);
      setShowEdit(null);
      carregar();
    } catch (err) { alert(err.response?.data?.detail || 'Erro ao editar'); }
  };

  const apagar = async (id) => {
    if (!window.confirm('Apagar este utilizador?')) return;
    await axios.delete(`${API}/auth/eliminar/${id}`);
    carregar();
  };

  const executarBackupAgora = async () => {
    try {
      await axios.post(`${API}/backup/auto/executar`);
      alert('Backup automático executado com sucesso!');
      carregar();
    } catch (err) { alert(err.response?.data?.detail || 'Erro no backup'); }
  };

  const executarBackupDiarioEmail = async () => {
    if (!window.confirm('Vai ser criado um backup diário e enviado por email para o administrador. Continuar?')) return;
    try {
      const r = await axios.post(`${API}/backup/diario/executar`);
      const res = r.data?.resultado || {};
      if (res.email_status === 'sent') alert('Backup diário criado e enviado por email com sucesso! 📧');
      else if (res.email_status === 'failed') alert('Backup criado, mas o envio do email falhou: ' + (res.email_error || ''));
      else alert('Backup diário criado (envio de email desativado).');
      carregar();
    } catch (err) { alert(err.response?.data?.detail || 'Erro no backup diário'); }
  };

  const restaurarBackup = async (nome) => {
    if (!window.confirm(`Tens a certeza que queres RESTAURAR o backup "${nome}"?\n\nOs dados atuais serão sobrescritos pelos dados do backup (upsert por ID).`)) return;
    try {
      const r = await axios.post(`${API}/backup/auto/restaurar/${encodeURIComponent(nome)}`);
      const counts = Object.entries(r.data.registos || {}).map(([k, v]) => `${k}: ${v}`).join(', ');
      alert(`Backup restaurado com sucesso!\n\nRegistos: ${counts}`);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao restaurar backup');
    }
  };

  const [auditoria, setAuditoria] = useState(null);
  const [showAuditoria, setShowAuditoria] = useState(false);
  const correrAuditoria = async () => {
    try {
      const r = await axios.get(`${API}/admin/auditoria-ficheiros-fantasma`);
      setAuditoria(r.data);
      setShowAuditoria(true);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro a correr auditoria');
    }
  };

  const exportarBackup = async () => {
    try {
      const r = await axios.get(`${API}/backup/exportar`, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 16);
      a.download = `AnimaLog_Backup_${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao exportar backup (verifica se tens permissões de Administrador)');
    }
  };

  const descarregarBackupAuto = async (nome) => {
    try {
      const r = await axios.get(`${API}/backup/auto/descarregar/${encodeURIComponent(nome)}`, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = nome;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao descarregar backup');
    }
  };

  const carregarAudit = async () => {
    try {
      const r = await axios.get(`${API}/audit/logs?limit=200`);
      setAuditLogs(r.data);
      setShowAudit(true);
    } catch (err) { alert(err.response?.data?.detail || 'Erro a obter logs'); }
  };

  const labelTipo = (t) => t === 'admin' ? 'Administrador' : t === 'tecnico' ? 'Assistente Técnico' : 'Assistente Operacional';
  const badgeTipo = (t) => t === 'admin' ? 'bg-destructive/10 text-destructive' : t === 'tecnico' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground';

  return (
    <div data-testid="gestao-page">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading font-bold text-2xl flex items-center gap-2">
          <Users className="w-6 h-6 text-primary" /> Gestão de Utilizadores
        </h2>
        <div className="flex gap-2">
          {canManageUsers && (
            <button onClick={() => setShowCreate(!showCreate)} data-testid="create-user-btn"
              className="flex items-center gap-1 bg-primary text-primary-foreground rounded-full px-4 py-2 text-sm font-medium hover:brightness-110 transition-all">
              <Plus className="w-4 h-4" /> Novo Utilizador
            </button>
          )}
        </div>
      </div>

      {/* Admin-only sections */}
      {isAdmin && (
        <>
          {/* Backup section */}
          <div className="bg-card rounded-2xl border border-border p-4 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <Shield className="w-4 h-4 text-destructive" />
              <h3 className="font-heading font-bold text-sm">Backup de Dados (Administrador)</h3>
            </div>
            <div className="flex flex-wrap gap-3">
              <button onClick={exportarBackup} data-testid="backup-export"
                className="flex items-center gap-1 bg-green-600 text-white rounded-full px-4 py-2 text-sm font-medium hover:brightness-110">
                <Download className="w-4 h-4" /> Exportar Backup (JSON)
              </button>
              <label className="flex items-center gap-1 bg-accent text-accent-foreground rounded-full px-4 py-2 text-sm font-medium cursor-pointer hover:brightness-110">
                <Upload className="w-4 h-4" /> Importar Backup
                <input type="file" accept=".json" className="hidden" onChange={async (e) => {
                  const file = e.target.files[0]; if (!file) return;
                  const text = await file.text();
                  try {
                    const data = JSON.parse(text);
                    await axios.post(`${API}/backup/importar`, data);
                    alert('Backup importado com sucesso!');
                  } catch (err) { alert('Erro ao importar: ' + (err.response?.data?.detail || err.message)); }
                }} />
              </label>
              <button onClick={executarBackupAgora} data-testid="backup-run-now"
                className="flex items-center gap-1 bg-primary text-primary-foreground rounded-full px-4 py-2 text-sm font-medium hover:brightness-110">
                <PlayCircle className="w-4 h-4" /> Executar Backup Semanal
              </button>
              <button onClick={executarBackupDiarioEmail} data-testid="backup-daily-email"
                className="flex items-center gap-1 bg-blue-600 text-white rounded-full px-4 py-2 text-sm font-medium hover:brightness-110">
                <Mail className="w-4 h-4" /> Backup Diário + Email
              </button>
              <button onClick={carregarAudit} data-testid="audit-view"
                className="flex items-center gap-1 bg-muted text-foreground rounded-full px-4 py-2 text-sm font-medium hover:brightness-110">
                <History className="w-4 h-4" /> Registo de Atividade
              </button>
              <button onClick={correrAuditoria} data-testid="audit-phantom-files"
                className="flex items-center gap-1 bg-orange-500 text-white rounded-full px-4 py-2 text-sm font-medium hover:brightness-110">
                <AlertTriangle className="w-4 h-4" /> Auditoria Ficheiros
              </button>
              <button onClick={async () => {
                try {
                  const r = await axios.get(`${API}/admin/codigo-fonte`, { responseType: 'blob' });
                  const blob = new Blob([r.data], { type: 'application/zip' });
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url; a.download = 'animalog_codigo_completo.zip';
                  document.body.appendChild(a); a.click(); a.remove();
                  window.URL.revokeObjectURL(url);
                } catch (err) { alert(err.response?.data?.detail || 'Erro a descarregar código'); }
              }} data-testid="download-source-zip"
                className="flex items-center gap-1 bg-purple-600 text-white rounded-full px-4 py-2 text-sm font-medium hover:brightness-110">
                <Download className="w-4 h-4" /> Descarregar Código (ZIP)
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">📅 Backup <b>diário automático</b> às 03:00 UTC (enviado por email para o administrador) · 📦 Backup <b>semanal</b> aos Domingos (no servidor) · 🖼️ Fotos armazenadas no Cloudinary CDN para segurança máxima.</p>
            {backups.length > 0 && (
              <div className="mt-3">
                <p className="text-xs font-medium mb-1">Últimos backups automáticos:</p>
                <ul className="text-xs text-muted-foreground space-y-1">
                  {backups.slice(0, 10).map(b => (
                    <li key={b.nome} className="flex items-center gap-2 flex-wrap">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${b.tipo === 'diario' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                        {b.tipo === 'diario' ? 'DIÁRIO' : 'SEMANAL'}
                      </span>
                      <button onClick={() => descarregarBackupAuto(b.nome)}
                        className="text-primary hover:underline inline-flex items-center gap-1"
                        data-testid={`download-auto-backup-${b.nome}`}>
                        <Download className="w-3 h-3" /> {b.nome}
                      </button>
                      <span>— {b.tamanho_kb} KB — {new Date(b.timestamp).toLocaleString('pt-PT')}</span>
                      <button onClick={() => restaurarBackup(b.nome)}
                        className="text-orange-600 hover:underline inline-flex items-center gap-1 ml-1"
                        data-testid={`restore-auto-backup-${b.nome}`}>
                        <RotateCcw className="w-3 h-3" /> Restaurar
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </>
      )}

      {msg && <div className="bg-green-100 text-green-800 p-3 rounded-xl mb-4 text-sm">{msg}</div>}

      {showCreate && canManageUsers && (
        <form onSubmit={criar} className="bg-card rounded-2xl border border-border p-4 mb-6 space-y-3">
          <h3 className="font-heading font-bold text-lg">Criar Utilizador</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input placeholder="Nome" value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} required className="rounded-xl border border-border bg-background px-3 py-2 text-sm" />
            <input type="email" placeholder="Email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required className="rounded-xl border border-border bg-background px-3 py-2 text-sm" />
            <PasswordInput value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required placeholder="Password" testid="create-user-password" />
            <select value={form.tipo} onChange={e => setForm({ ...form, tipo: e.target.value })} className="rounded-xl border border-border bg-background px-3 py-2 text-sm">
              <option value="operacional">Assistente Operacional</option>
              <option value="tecnico">Assistente Técnico</option>
              {isAdmin && <option value="admin">Administrador</option>}
            </select>
          </div>
          <button type="submit" className="bg-primary text-primary-foreground rounded-full px-4 py-2 text-sm font-medium hover:brightness-110">Criar</button>
        </form>
      )}

      <div className="bg-card rounded-2xl border border-border overflow-hidden">
        {/* Desktop table */}
        <table className="hidden md:table w-full text-sm">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="text-left p-3 font-medium">Nome</th>
              <th className="text-left p-3 font-medium">Email</th>
              <th className="text-left p-3 font-medium">Tipo</th>
              {canManageUsers && <th className="text-left p-3 font-medium">Ações</th>}
            </tr>
          </thead>
          <tbody>
            {users.map(u => {
              const canActOnThis = isAdmin || u.tipo !== 'admin';
              return (
              <tr key={u.id} className="border-b border-border hover:bg-muted/30 transition-colors">
                <td className="p-3 font-medium">{u.nome}</td>
                <td className="p-3">{u.email}</td>
                <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded-full ${badgeTipo(u.tipo)}`}>{labelTipo(u.tipo)}</span></td>
                {canManageUsers && (
                  <td className="p-3 flex gap-1">
                    {canActOnThis ? (<>
                      <button onClick={() => { setShowEdit(u); setEditForm({ nome: u.nome, email: u.email, tipo: u.tipo, novaPassword: '' }); }} className="text-primary hover:bg-primary/10 p-1.5 rounded-full"><Edit className="w-4 h-4" /></button>
                      <button onClick={() => apagar(u.id)} className="text-destructive hover:bg-destructive/10 p-1.5 rounded-full"><Trash2 className="w-4 h-4" /></button>
                    </>) : (
                      <span className="text-xs text-muted-foreground italic">—</span>
                    )}
                  </td>
                )}
              </tr>
            );})}
          </tbody>
        </table>
        {/* Mobile cards */}
        <div className="md:hidden divide-y divide-border">
          {users.map(u => {
            const canActOnThis = isAdmin || u.tipo !== 'admin';
            return (
              <div key={u.id} className="p-4 flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{u.nome}</p>
                  <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                  <span className={`inline-block mt-1.5 text-[10px] px-2 py-0.5 rounded-full ${badgeTipo(u.tipo)}`}>{labelTipo(u.tipo)}</span>
                </div>
                {canManageUsers && (
                  <div className="flex gap-2 flex-shrink-0">
                    {canActOnThis ? (<>
                      <button onClick={() => { setShowEdit(u); setEditForm({ nome: u.nome, email: u.email, tipo: u.tipo, novaPassword: '' }); }}
                        className="bg-primary/10 text-primary p-2 rounded-full" data-testid={`edit-user-${u.id}`}>
                        <Edit className="w-4 h-4" />
                      </button>
                      <button onClick={() => apagar(u.id)}
                        className="bg-destructive/10 text-destructive p-2 rounded-full" data-testid={`delete-user-${u.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>) : (
                      <span className="text-xs text-muted-foreground italic">—</span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {showEdit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-card rounded-2xl border border-border p-6 w-full max-w-sm m-4 space-y-3">
            <div className="flex justify-between items-center"><h3 className="font-heading font-bold text-lg">Editar Utilizador</h3><button onClick={() => setShowEdit(null)}><X className="w-5 h-5" /></button></div>
            <input value={editForm.nome} onChange={e => setEditForm({ ...editForm, nome: e.target.value })} className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm" placeholder="Nome" />
            <input type="email" value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })} className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm" placeholder="Email" />
            <PasswordInput value={editForm.novaPassword} onChange={e => setEditForm({ ...editForm, novaPassword: e.target.value })} placeholder="Nova Password (opcional)" testid="edit-user-password" />
            <select value={editForm.tipo} onChange={e => setEditForm({ ...editForm, tipo: e.target.value })} className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm">
              <option value="operacional">Assistente Operacional</option>
              <option value="tecnico">Assistente Técnico</option>
              {isAdmin && <option value="admin">Administrador</option>}
            </select>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowEdit(null)} className="text-xs bg-muted text-foreground rounded-full px-3 py-1.5">Cancelar</button>
              <button onClick={editar} className="text-xs bg-primary text-primary-foreground rounded-full px-3 py-1.5">Guardar</button>
            </div>
          </div>
        </div>
      )}

      {showAudit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-card rounded-2xl border border-border shadow-2xl w-full max-w-3xl m-4 max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="font-heading font-bold text-lg flex items-center gap-2"><History className="w-4 h-4" /> Registo de Atividade</h3>
              <button onClick={() => setShowAudit(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 overflow-y-auto">
              {auditLogs.length === 0 ? (
                <p className="text-sm text-muted-foreground">Sem registos.</p>
              ) : (
                <table className="w-full text-xs">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr>
                      <th className="text-left p-2">Quando</th>
                      <th className="text-left p-2">Quem</th>
                      <th className="text-left p-2">Ação</th>
                      <th className="text-left p-2">Detalhes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogs.map(l => (
                      <tr key={l.id} className="border-b border-border/60">
                        <td className="p-2 whitespace-nowrap">{new Date(l.timestamp).toLocaleString('pt-PT')}</td>
                        <td className="p-2">{l.user_email || 'sistema'}<br /><span className="text-muted-foreground">{l.user_role || ''}</span></td>
                        <td className="p-2 font-medium">{l.action}</td>
                        <td className="p-2 text-muted-foreground"><code className="text-[10px]">{l.target_type || ''} {l.target_id ? `#${l.target_id.slice(0, 8)}` : ''} {l.details ? JSON.stringify(l.details).slice(0, 90) : ''}</code></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {showAuditoria && auditoria && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-card rounded-2xl border border-border shadow-2xl w-full max-w-3xl m-4 max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="font-heading font-bold text-lg flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-orange-500" /> Auditoria de Ficheiros</h3>
              <div className="flex items-center gap-2">
                <button onClick={async () => {
                  try {
                    const r = await axios.get(`${API}/admin/auditoria-ficheiros-fantasma/pdf`, { responseType: 'blob' });
                    const blob = new Blob([r.data], { type: 'application/pdf' });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 16);
                    a.href = url; a.download = `AnimaLog_Auditoria_${stamp}.pdf`;
                    document.body.appendChild(a); a.click(); a.remove();
                    window.URL.revokeObjectURL(url);
                  } catch (err) { alert(err.response?.data?.detail || 'Erro a gerar PDF'); }
                }} data-testid="export-audit-pdf"
                  className="flex items-center gap-1 bg-red-600 text-white rounded-full px-3 py-1.5 text-xs font-medium hover:brightness-110">
                  <Download className="w-3.5 h-3.5" /> Exportar PDF
                </button>
                <button onClick={() => setShowAuditoria(false)}><X className="w-5 h-5" /></button>
              </div>
            </div>
            <div className="p-4 overflow-y-auto space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                <div className="bg-muted/50 rounded-lg p-3"><div className="text-muted-foreground">Total animais</div><div className="text-2xl font-bold">{auditoria.total_animais}</div></div>
                <div className="bg-green-50 rounded-lg p-3"><div className="text-muted-foreground">Com fotos no Cloudinary</div><div className="text-2xl font-bold text-green-700">{auditoria.com_fotos_cloudinary}</div></div>
                <div className="bg-blue-50 rounded-lg p-3"><div className="text-muted-foreground">Com fotos base64</div><div className="text-2xl font-bold text-blue-700">{auditoria.com_fotos_base64}</div></div>
                <div className="bg-orange-50 rounded-lg p-3"><div className="text-muted-foreground">Sem fotos</div><div className="text-2xl font-bold text-orange-700">{auditoria.sem_qualquer_foto}</div></div>
              </div>

              <div>
                <h4 className="font-bold text-sm mb-2 text-orange-600">🖼️ Animais com fotos fantasma ({auditoria.animais_com_fotos_fantasma.length})</h4>
                <p className="text-xs text-muted-foreground mb-2">Referências a ficheiros antigos do disco — precisam de re-upload.</p>
                {auditoria.animais_com_fotos_fantasma.length === 0 ? (
                  <p className="text-xs text-green-600">✅ Nenhum animal com fotos fantasma.</p>
                ) : (
                  <ul className="text-xs space-y-1 max-h-60 overflow-y-auto border border-border rounded-lg p-2">
                    {auditoria.animais_com_fotos_fantasma.map(a => (
                      <li key={a.id} className="flex justify-between border-b border-border/30 py-1">
                        <span className="font-medium">{a.nome}</span>
                        <span className="text-muted-foreground">{a.qtd} foto(s) perdida(s)</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <h4 className="font-bold text-sm mb-2 text-orange-600">📄 Animais com ficheiros/PDFs fantasma ({auditoria.animais_com_ficheiros_fantasma.length})</h4>
                {auditoria.animais_com_ficheiros_fantasma.length === 0 ? (
                  <p className="text-xs text-green-600">✅ Nenhum animal com ficheiros fantasma.</p>
                ) : (
                  <ul className="text-xs space-y-1 max-h-60 overflow-y-auto border border-border rounded-lg p-2">
                    {auditoria.animais_com_ficheiros_fantasma.map(a => (
                      <li key={a.id} className="flex justify-between border-b border-border/30 py-1">
                        <span className="font-medium">{a.nome}</span>
                        <span className="text-muted-foreground">{a.qtd} ficheiro(s) perdido(s)</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
